import React from "react";
import { Collapse, Form, Row, Col, Select, Input, Space, Button, FormInstance, Spin } from "antd";
import MaskedInput from "antd-mask-input";
import { RootStateOrAny, useSelector } from "react-redux";
import isEqual from "lodash/isEqual";
import debounce from "lodash/debounce";
import { IindividualSearchValues } from "../../lib/interfaces/IindividualSearchValues";
import { AnyObject } from "../../lib/interfaces/AnyObject";
import usePhoneMask from "../../lib/hooks/usePhoneMask";
import { checkIsLoading } from "../../lib/utils";
import { useAppDispatch, useAppSelector } from "../..";
import { accountTsActions, fetchAccountsIndividualByName } from "../../store/slices/accountsTs";

const { Panel } = Collapse;
const { Option } = Select;

interface ISearchFiltersIndividual {
  handlePageableSearch: (props: IindividualSearchValues) => void;
  handleSetIndividualSearchValues: (prop: AnyObject) => void;
  individualSearchValues: IindividualSearchValues;
  initialIndividualSearchValues: IindividualSearchValues;
  searchIndividualForm: FormInstance;
  resetSearchFullName: () => void;
}

function SearchFiltersIndividual(props: ISearchFiltersIndividual) {
  const {
    handlePageableSearch,
    handleSetIndividualSearchValues,
    individualSearchValues,
    initialIndividualSearchValues,
    searchIndividualForm,
    resetSearchFullName,
  } = props;
  const dispatch = useAppDispatch();
  const { accountsIndividualSelect, accountsIndividualSelectLoading } = useAppSelector(
    (state) => state.accountsTs
  );

  const { accountStatuses, accountTypes } = useSelector((state: RootStateOrAny) => state.dicts);
  const searchNotPerformed = isEqual(individualSearchValues, initialIndividualSearchValues);

  const { phoneMask, cleanFullPhoneNumber } = usePhoneMask();

  const searchAccountsByName = (fullName: string) => {
    if (fullName) {
      dispatch(fetchAccountsIndividualByName(fullName));
    }
  };

  const debouncedSearchAccountsByName = debounce(searchAccountsByName, 800);

  const onCleanIndividualFullName = () => {
    dispatch(accountTsActions.cleanAccountsIndividualSelect());
  };

  const onFinish = (values) => {
    const fullName = values?.fullName?.split(" ").filter(Boolean).join(" "); // удаление лишних пробелов
    const id = values?.id?.split(";");
    const agreementId = values?.agreementId?.split(";");
    const phone = values?.phone && cleanFullPhoneNumber(values?.phone);
    const searchValues = { ...values, fullName, page: 0, id, agreementId, phone };
    handleSetIndividualSearchValues(searchValues);
    handlePageableSearch(searchValues);
    resetSearchFullName();
  };

  const onReset = () => {
    searchIndividualForm.setFieldsValue(initialIndividualSearchValues);
    // Запрос аккаунтов в контейнере происходит по смене номера страницы, если при сбросе номер страницы не меняется - здесь
    handlePageableSearch(initialIndividualSearchValues);
    handleSetIndividualSearchValues(initialIndividualSearchValues);
  };

  return (
    <Collapse expandIconPosition="right" defaultActiveKey={searchNotPerformed ? 0 : 1}>
      <Panel header="Расширенный поиск" key="1">
        <Form
          style={{ marginTop: 20 }}
          form={searchIndividualForm}
          onFinish={onFinish}
          wrapperCol={{ span: 19 }}
          initialValues={individualSearchValues}
          labelAlign="left"
        >
          <Row gutter={50} justify="center">
            <Col span={11}>
              <Form.Item name="fullName" label="ФИО" labelCol={{ span: 6 }}>
                <Select
                  showSearch
                  allowClear
                  notFoundContent={
                    checkIsLoading(accountsIndividualSelectLoading) && <Spin size="small" />
                  }
                  onSearch={debouncedSearchAccountsByName}
                  onClear={onCleanIndividualFullName}
                >
                  {accountsIndividualSelect.map((item) => {
                    const fullName = [
                      item?.individual?.lastName,
                      item?.individual?.firstName,
                      item?.individual?.middleName,
                    ]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <Select.Option key={item.id} value={fullName} title={fullName}>
                        {fullName}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
              <Form.Item
                name="phone"
                label="Контактный телефон"
                initialValue=""
                labelCol={{ span: 6 }}
              >
                <MaskedInput
                  mask={phoneMask.full}
                  aria-autocomplete="none"
                  name="phone"
                  allowClear
                />
              </Form.Item>
              <Form.Item name="externalId" label="Технический ID" labelCol={{ span: 6 }}>
                <Input allowClear />
              </Form.Item>
              <Form.Item name="email" label="Email" labelCol={{ span: 6 }}>
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={11}>
              <Form.Item name="agreementNumber" label="Номер договора" labelCol={{ span: 5 }}>
                <Input aria-autocomplete="none" allowClear />
              </Form.Item>
              <Form.Item name="accountStatusId" label="Статус аккаунта" labelCol={{ span: 5 }}>
                <Select allowClear mode="multiple" showArrow>
                  {accountStatuses.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="accTypeId" label="Тип аккаунта" labelCol={{ span: 5 }}>
                <Select allowClear mode="multiple" showArrow>
                  {accountTypes.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="agreementId"
                label="ID договоров"
                rules={[
                  {
                    validator: (x, value) => {
                      if (value && /[^0-9,]/.test(value)) {
                        return Promise.reject();
                      }
                      return Promise.resolve();
                    },
                    message: "В качестве разделителя может использоваться только запятая",
                  },
                ]}
                labelCol={{ span: 5 }}
              >
                <Input allowClear placeholder='Введите ID договоров через ","' />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={50} justify="center">
            <Col span={11}>
              <Form.Item
                name="id"
                label="ID аккаунтов"
                rules={[
                  {
                    validator: (x, value) => {
                      if (value && /[^0-9,]/.test(value)) {
                        return Promise.reject();
                      }
                      return Promise.resolve();
                    },
                    message: "В качестве разделителя может использоваться только запятая",
                  },
                ]}
                labelCol={{ span: 6 }}
              >
                <Input
                  aria-autocomplete="none"
                  allowClear
                  placeholder='Введите ID аккаунтов через ","'
                />
              </Form.Item>
            </Col>
            <Col span={11} />
          </Row>
          <Row justify="end">
            <Space>
              <Button htmlType="reset" onClick={onReset}>
                Сброс параметров
              </Button>

              <Button type="primary" htmlType="submit">
                Применить
              </Button>
            </Space>
          </Row>
        </Form>
      </Panel>
    </Collapse>
  );
}

export default SearchFiltersIndividual;
